import { motion } from "framer-motion";

/**
 * Abstract decorative orbits + floating geometric shapes.
 * Pure SVG / CSS — GPU friendly, no canvas.
 */
export const OrbitRings = ({ className = "" }) => {
    return (
        <div
            aria-hidden
            className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
            data-testid="orbit-rings"
        >
            {/* Concentric rings */}
            <motion.svg
                viewBox="0 0 800 800"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-[20rem] sm:w-[120%] sm:max-w-none lg:w-[140%] lg:h-[140%] h-auto aspect-square opacity-20 sm:opacity-30"
                animate={{ rotate: 360 }}
                transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
            >
                <defs>
                    <linearGradient id="ring1" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="ring2" x1="1" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="ring3" x1="0" y1="1" x2="1" y2="0">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.7" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <circle
                    cx="400"
                    cy="400"
                    r="380"
                    fill="none"
                    stroke="url(#ring1)"
                    strokeWidth="0.6"
                    strokeDasharray="2 6"
                />
                <circle
                    cx="400"
                    cy="400"
                    r="300"
                    fill="none"
                    stroke="url(#ring2)"
                    strokeWidth="0.6"
                    strokeDasharray="1 10"
                />
                <circle
                    cx="400"
                    cy="400"
                    r="230"
                    fill="none"
                    stroke="url(#ring3)"
                    strokeWidth="0.5"
                />
                {/* Orbit nodes */}
                <circle cx="780" cy="400" r="4" fill="#06b6d4" />
                <circle cx="20" cy="400" r="3" fill="#fbbf24" />
                <circle cx="400" cy="100" r="3" fill="#10b981" />
            </motion.svg>

            {/* Counter-rotating inner ring */}
            <motion.svg
                viewBox="0 0 800 800"
                className="hidden sm:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] opacity-25"
                animate={{ rotate: -360 }}
                transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
            >
                <circle
                    cx="400"
                    cy="400"
                    r="180"
                    fill="none"
                    stroke="rgba(255,255,255,0.18)"
                    strokeWidth="0.5"
                    strokeDasharray="4 8"
                />
                <circle cx="580" cy="400" r="2.5" fill="#a78bfa" />
            </motion.svg>

            {/* Floating geometric shapes — desktop only */}
            <motion.div
                animate={{ y: [0, -20, 0], rotate: [0, 90, 0] }}
                transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
                className="hidden sm:block absolute top-[12%] right-[10%] w-12 h-12 border border-cyan-400/30 rounded-md backdrop-blur-sm"
                style={{ transform: "rotate(15deg)" }}
            />
            <motion.div
                animate={{ y: [0, 24, 0] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
                className="hidden sm:block absolute bottom-[20%] left-[8%] w-3 h-3 bg-emerald-400/40 rounded-full shadow-[0_0_20px_rgba(16,185,129,0.5)]"
            />
            <motion.div
                animate={{ y: [0, -16, 0], x: [0, 10, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
                className="hidden sm:block absolute top-[40%] left-[5%] w-8 h-8 border-2 border-amber-glow/30 rotate-45"
            />
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="hidden sm:block absolute bottom-[15%] right-[12%] w-10 h-10 border border-white/10 rounded-full"
            >
                <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-cyan-400 rounded-full shadow-[0_0_12px_rgba(34,211,238,0.7)]" />
            </motion.div>
            <motion.div
                animate={{ y: [0, 18, 0], rotate: [0, -20, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                className="hidden sm:block absolute top-[60%] right-[6%] w-2 h-2 bg-violet-400 rounded-full shadow-[0_0_16px_rgba(167,139,250,0.6)]"
            />
        </div>
    );
};
